import PropTypes from 'prop-types'

import styles from './detail.module.scss'

const DATEIGROESSE = bytes => {
	if (!bytes) return ''
	const mb = bytes / 1024 / 1024
	return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Alle Dateien einer Anforderung an einem Ort — zum Ansehen, nicht zum Ablegen.
 *
 * Bis 22.08.2026 war dieser Reiter zugleich die Ablagefläche: Wer ein Bild
 * hinzufügen wollte, tat es hier, und das Bild landete im gemeinsamen Stapel,
 * losgelöst von dem Satz, der es erklärt. Neue Dateien kommen jetzt dort
 * herein, wo ohnehin geschrieben wird — am Kommentarfeld, gezogen oder mit
 * Strg+V eingefügt, mitsamt einer Beschreibung.
 *
 * Was bleibt, ist das Archiv: **alle** Dateien der Anforderung, die des
 * Stapels und die an den Beiträgen. Genau deshalb ist der Reiter nützlich —
 * die Beitrags-Anhänge liegen sonst verstreut über eine lange Zeitleiste, und
 * „war da nicht ein Bildschirmfoto?" bedeutete, sie ganz durchzusehen.
 *
 * Ein Klick öffnet die Vergrößerung; dort blättern ←/→ durch **alle** Bilder
 * der Anforderung (`UI/Lightbox`). Ein PDF oder ein Video wird nicht
 * vergrößert, sondern heruntergeladen — der Browser zeigt beides besser als
 * eine nachgebaute Vorschau.
 */
export default function AttachmentLibrary({ dateien, urls, bildIndex, onOpen }) {
	if (!dateien.length) {
		return (
			<p className={styles.emptyNote}>
				Noch keine Dateien. Bilder und PDF kommen über das Kommentarfeld im Reiter
				„Anforderung" herein — gezogen, mit Strg+V eingefügt oder über „Anhang". Hier
				stehen sie danach alle beieinander.
			</p>
		)
	}

	return (
		<div className={styles.thumbs}>
			{dateien.map((shot, index) => {
				const url = urls[shot.id]
				const name = shot.caption || shot.originalName || `Datei ${index + 1}`
				const istBild = (shot.mimeType || '').startsWith('image/')
				// Woher die Datei kommt: aus dem Stapel der Anforderung oder von
				// einem Beitrag. Ohne das stünden im Archiv Bilder ohne
				// erkennbaren Zusammenhang nebeneinander.
				const herkunft = shot.commentId ? 'aus einem Kommentar' : 'zur Anforderung'

				return (
					<figure key={shot.id} className={styles.thumb}>
						{istBild && url ? (
							<button
								type="button"
								className={styles.thumbBtn}
								title="Vergrößern — ←/→ blättert"
								onClick={() => onOpen(bildIndex[shot.id])}
							>
								<img className={styles.thumbImg} src={url} alt={name} />
							</button>
						) : (
							<div className={styles.thumbFile}>
								<span className={styles.thumbBadge} aria-hidden="true">
									{istBild ? '…' : (shot.mimeType || '').split('/')[1]?.toUpperCase() || 'DATEI'}
								</span>
							</div>
						)}

						<figcaption className={styles.thumbCaption}>
							<span className={styles.thumbName} title={name}>
								{name}
							</span>

							{/*
							 * Der Satz zur Datei — der eigentliche Grund, warum das
							 * Archiv mehr ist als eine Reihe Vorschaubilder. Ein
							 * Bildschirmfoto ohne ihn ist beim Nachlesen stumm.
							 */}
							{shot.description && (
								<span className={styles.thumbDescription}>{shot.description}</span>
							)}

							<span className={styles.thumbMeta}>
								{herkunft}
								{shot.size ? ` · ${DATEIGROESSE(shot.size)}` : ''}
							</span>

							{url && (
								<a
									className={styles.thumbDownload}
									href={url}
									download={shot.caption || shot.originalName || `anhang-${index + 1}`}
									title="Herunterladen"
								>
									Herunterladen
								</a>
							)}
						</figcaption>
					</figure>
				)
			})}
		</div>
	)
}

AttachmentLibrary.propTypes = {
	dateien: PropTypes.arrayOf(PropTypes.object).isRequired,
	urls: PropTypes.object.isRequired,
	// Kennung → Platz in der Bilderfolge der Vergrößerung. Nicht die Position in
	// dieser Liste: PDF und Videos lassen sich nicht vergrößern und zählen dort
	// nicht mit.
	bildIndex: PropTypes.object.isRequired,
	onOpen: PropTypes.func.isRequired
}
